import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import TrackingAPI from 'api/TrackingAPI';
import crown_img from 'assets/crown.svg';
import PicturesCountIcon from 'assets/pictures_count_icon.svg?react';
import token_icon from 'assets/token.svg';
import VideoCountIcon from 'assets/video_count_icon.svg?react';
import BuyTokensModal from 'modals/BuyTokensModal';
import MediaPackGridModal from 'modals/MediaPackGridModal/MediaPackGridModal';
import { useMappedState } from 'redux-react-hook';
import { analyticsService } from 'services/analyticsService';
import styled from 'styled-components';
import Spinner from 'ui-kit/Spinner';
import { isBlank } from 'utils/storeUtils';

import ConfirmTokenMediaPackModal from './components/ConfirmTokenMediaPackModal';

interface MediaItem {
  url: string;
  videoCoverUrl?: string;
  isVideo?: boolean;
  order?: number;
}

interface MediaPack {
  id: string;
  coverUrl: string;
  title: string;
  mediaItems?: MediaItem[];
  type: string;
  price?: number;
  _id: string;
  isPurchased?: boolean;
  picturesCount?: number;
  videosCount?: number;
  isActive?: boolean;
}

interface IMediaPackCardProps {
  mediaPack: MediaPack;
  isLoading?: boolean;
  error?: string;
  setIsSubscriptionModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsSignUpModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsEmailConfirmationModalOpen: Dispatch<SetStateAction<boolean>>;
  game?: Record<string, unknown>;
  scenario?: Record<string, unknown>;
  characterName?: string;
  className?: string;
  renderReason?: string;
}

const MediaPackCard: React.FC<IMediaPackCardProps> = (props) => {
  const {
    mediaPack,
    isLoading = false,
    error,
    setIsSubscriptionModalOpen,
    setIsSignUpModalOpen,
    setIsEmailConfirmationModalOpen,
    game,
    scenario,
    characterName,
    className,
  } = props;
  const [isMediaPackGalleryModalOpen, setIsMediaPackGalleryModalOpen] = useState(false);
  const [isBuyTokensModalOpen, setIsBuyTokensModalOpen] = useState(false);
  const [isConfirmTokenMediaPackModalOpen, setIsConfirmTokenMediaPackModalOpen] = useState(false);

  const { hasSubscription, currentUser } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        hasSubscription: hasSubscription,
        currentUser: currentUser
      };
    }, [])
  );

  const tokenBalance = currentUser?.tokenBalance ?? 0.0;

  const isFree = mediaPack.type === 'free';
  const isPremium = mediaPack.type === 'premium';
  const isPaid = mediaPack.type === 'paid';

  const shouldBlurPremium = !hasSubscription && isPremium;
  const shouldBlurPaid = isPaid && !mediaPack.isPurchased;
  const shouldBlur = shouldBlurPremium || shouldBlurPaid;

  const hasVideoItems = mediaPack.videosCount! > 0;

  const analyticsData = {
    config: game || {},
    config_name: scenario?.name || '',
    scenario_name: game?.name || '',
    scenarioId: String(scenario?.id || ''),
    characterName: characterName || '',
    mediaPackId: mediaPack._id
  };



  const handleMediaPackClick = () => {
    if (isLoading) return;

    TrackingAPI.trackEvent('media_pack_click', analyticsData);
    analyticsService.trackGoogleEvent('media_pack_click', analyticsData);
    if (isBlank(currentUser)) {
      setIsSignUpModalOpen(true);
      return;
    }

    if (!isBlank(currentUser) && !currentUser?.emailVerified) {
      setIsEmailConfirmationModalOpen(true);
      return;
    }

    if (isPremium && !hasSubscription) {
      setIsSubscriptionModalOpen(true);
      return;
    } else if (isPaid && !mediaPack.isPurchased) {
      if (tokenBalance < (mediaPack?.price ?? 0)) {
        setIsBuyTokensModalOpen(true);
        return;
      } else {
        setIsConfirmTokenMediaPackModalOpen(true);
        return;
      }
    }

    setIsMediaPackGalleryModalOpen(true);
  };

  if (error) {
    return (
      <ErrorContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ErrorContainer>
    );
  }

  if (!mediaPack || (isPaid && !hasSubscription)) {
    return null;
  }

  return (
    <>
      <CardWrapper className={className} onClick={handleMediaPackClick}>
        {isLoading ? (
          <LoadingContainer>
            <Spinner size={40} />
          </LoadingContainer>
        ) : (
          <>
            <CardImage
              src={mediaPack.coverUrl}
              alt={mediaPack.title}
              $blurred={shouldBlur}
            />
            <CardContent $isPaid={isPaid && shouldBlurPaid}>
              <ItemCount>
                {hasVideoItems && <><VideoCountIcon />{mediaPack.videosCount || 0}</>}
                <PicturesCountIcon />{mediaPack.picturesCount || 0}
              </ItemCount>
              <Title $isPaid={isPaid && shouldBlurPaid}>{mediaPack.title}</Title>
              {isPaid && shouldBlurPaid && (
                <TokenLabel>
                  <TokenIcon src={token_icon} alt="Token" />
                  {mediaPack.price} tokens
                </TokenLabel>
              )}
            </CardContent>
            {isFree && <FreeLabel>Free</FreeLabel>}
            {isPremium && (
              <>
                {!hasSubscription && (
                  <PremiumLabel>
                    <img style={{ height: '9px' }} src={crown_img} />
                    Premium
                  </PremiumLabel>
                )}
                {hasSubscription && (
                  <PremiumChip>
                    <img style={{ height: '9px' }} src={crown_img} />
                  </PremiumChip>
                )}
              </>
            )}
          </>
        )}
      </CardWrapper>

      {isMediaPackGalleryModalOpen && mediaPack.mediaItems && (
        <MediaPackGridModal
          title={mediaPack.title}
          mediaItems={mediaPack.mediaItems}
          onClose={() => setIsMediaPackGalleryModalOpen(false)}
        />
      )}
      {isBuyTokensModalOpen && (
        <BuyTokensModal
          isOpen
          onClose={() => setIsBuyTokensModalOpen(false)}
          analyticsDataRenderReason='hot_media_pack_click'
          analyticsData={analyticsData}
        />
      )}
      {isConfirmTokenMediaPackModalOpen && (
        <ConfirmTokenMediaPackModal
          isVisible={isConfirmTokenMediaPackModalOpen}
          onClose={() => setIsConfirmTokenMediaPackModalOpen(false)}
          mediaPackId={mediaPack._id}
          analyticsData={{
            config: game || {},
            scenarioId: String(scenario?.id || ''),
            characterName: characterName || '',
            mediaPackId: mediaPack._id
          }}
        />
      )}
    </>
  );
};

export default MediaPackCard;

export type { MediaPack };

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 128px;
  border-radius: 12px;
  overflow: hidden;
  background: #1a1a1a;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const CardImage = styled.img<{ $blurred?: boolean }>`
  width: 101%;
  height: 101%;
  object-fit: cover;
  object-position: center;
  ${props => props.$blurred && 'filter: blur(4px);'}
`;

const CardContent = styled.div<{ $isPaid?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${props => props.$isPaid ? '0' : '12px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  height: ${props => props.$isPaid ? '100%' : 'auto'};
  justify-content: ${props => props.$isPaid ? 'center' : 'flex-end'};
  padding: ${props => props.$isPaid ? '20px 0' : '0'};
  background: ${props => props.$isPaid ? 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)' : 'none'};
`;

const ItemCount = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #FFFFFF;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h3<{ $isPaid?: boolean }>`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  color: #FFFFFF;
  margin: 0;
  max-width: 158px;
  margin-bottom: ${props => props.$isPaid ? '8px' : '0'};
`;

const FreeLabel = styled.div`
  display: inline-flex;
  height: 24px;
  padding: 6px 12px;
  align-items: center;
  gap: 10px;
  border-radius: 64px;
  background: #FF7E55;
  position: absolute;
  top: 12px;
  right: 12px;
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
`;

const PremiumLabel = styled.div`
  color: white;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  border-radius: 64px;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  padding: 6px 12px;
  align-items: center;
  gap: 4px;
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
`;

const PremiumChip = styled(PremiumLabel)`
  padding: 6px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

const ErrorContainer = styled(CardWrapper)`
  background: #2A1A1A;
  cursor: default;

  &:hover {
    transform: none;
  }
`;

const ErrorMessage = styled.p`
  color: #FF4444;
  text-align: center;
  padding: 20px;
  margin: 0;
  font-family: 'Nunito Sans';
  font-size: 14px;
`;

const TokenLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 4px;
  padding: 6px 12px;
  background: #1D1D1D;
  border-radius: 64px;
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
`;

const TokenIcon = styled.img`
  width: 16px;
  height: 16px;
`;